import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useNotification } from "../../toster-notification/NotificationContext";
import Loader from "../../shared/Loader";
import { orderService } from "../../../services/orderService";
import { LiveOrdersDineInListItemDTO } from "../../../models/order/order.response";
import { handleErrorNotification } from "../../../utils/errorNotificatioMessage";
import { OrderStatusType, orderStatusTypeToClassName, orderStatusTypeToString } from "../../../models/base/OrderStatusType";
import { ArrowRightGreyIcon, ClocckGreyIcon, TableGreyIcon, UserGreyIcon } from "../../../assets/icons";
import { ToLocalDateTimeString } from "../../../utils/dateFormater";
import SideOrderInfo from "../shared/order-list/SideOrderInfo";
import OrderInfoModalView from "../shared/views/modal-view/OrderInfoModalView";
import { connection, startConnection } from "../../../services/signalRConnection";
import { requestBrowserNotificationPermission, playNotificationSound, showBrowserNotification, unlockNotificationSound } from "../../../notification/notificationService";


const LiveOrdersDineInPage: React.FC = () => {

    const [orders, setOrders] = useState<LiveOrdersDineInListItemDTO[]>([]);
    const [selectedOrderId, setSelectedOrderId] = useState<string>("");

    const [isOrderInfoModalOpen, setIsOrderInfoModalOpen] = useState<boolean>(false);

    const [notificationsEnabled, setNotificationsEnabled] = useState(Notification.permission === "granted");
    const [loading, setLoading] = useState<boolean>(false);
    const { notify } = useNotification();
    const { localId } = useParams<{ localId: string }>();

    useEffect(() => {
        fetchOrders();
    }, [localId]);

    useEffect(() => {

        if (!localId) {
            return;
        }

        const handleBillRequested = async (
            orderId: string,
            tableId: string
        ) => {

            await handleOrderNotification(
                "bill-requested",
                "Zatražen račun",
                `Porudžbina #${orderId.slice(0, 8)} je zatražila račun.`,
                orderId
            );

            fetchOrders();
        };


        const handleNewOrder = async (
            orderId: string
        ) => {

            await fetchOrder(orderId);

            await handleOrderNotification(
                "new-order",
                "Nova porudžbina",
                `Stigla je nova porudžbina #${orderId.slice(0, 8)}.`,
                orderId
            );
        };


        const setup = async () => {

            await startConnection();

            await connection.invoke<void>(
                "JoinOrderDineInMonitoring",
                localId
            );

            connection.on(
                "BillRequested",
                handleBillRequested
            );

            connection.on(
                "NewDineInOrder",
                handleNewOrder
            );
        };


        setup();


        return () => {

            connection.off(
                "BillRequested",
                handleBillRequested
            );

            connection.off(
                "NewDineInOrder",
                handleNewOrder
            );
        };

    }, [localId]);

    const fetchOrder = async (orderId: string) => {
        if (!localId) {
            notify("error", "Local ID nije prosleđen");
            return;
        }

        try {
            const response = await orderService.getDineInLiveOrder(localId, orderId);

            if (response.success && response.data) {
                const order = response.data;
                setOrders(prev => [...prev, order]);
            } else {
                notify("error", "Greška pri učitavanju porudžbina", response.message || "Došlo je do greške pri učitavanju porudžbina.");
            }
        } catch (error: any) {
            notify("error", "Greška pri učitavanju porudžbina", "Došlo je do greške pri učitavanju porudžbina.");
            handleErrorNotification(error, notify);
        }
    };

    const fetchOrders = async () => {
        if (!localId) {
            notify("error", "Local ID nije prosleđen");
            return;
        }

        setLoading(true);
        try {
            const response = await orderService.getDineInLiveOrders(localId);

            if (response.success && response.data) {
                setOrders(response.data);
            } else {
                notify("error", "Greška pri učitavanju porudžbina", response.message || "Došlo je do greške pri učitavanju porudžbina.");
            }
        } catch (error: any) {
            notify("error", "Greška pri učitavanju porudžbina", "Došlo je do greške pri učitavanju porudžbina.");
            handleErrorNotification(error, notify);
        } finally {
            setLoading(false);
        }
    };


    const enableNotifications = async () => {

        const granted = await requestBrowserNotificationPermission();

        setNotificationsEnabled(granted);

        if (granted) {

            // Kratko pokrenemo zvuk kao rezultat user interaction-a.
            // Tako browser zna da korisnik dozvoljava audio.
            await unlockNotificationSound();

            notify(
                "success",
                "Obaveštenja uključena",
                "Primaćete zvučna obaveštenja za nove porudžbine."
            );
        }
    };

    const handleOrderNotification = async (
        type:
            | "new-order"
            | "bill-requested"
            | "order-ready"
            | "order-cancelled",
        title: string,
        message: string,
        orderId?: string
    ) => {

        // 1. Zvuk
        await playNotificationSound(type);

        // 2. Tvoja postojeća notifikacija unutar React aplikacije
        notify(
            type === "order-cancelled" ? "error" : "info",
            title,
            message
        );

        // 3. System notification samo ako korisnik
        // trenutno ne gleda aplikaciju
        if (document.visibilityState !== "visible") {

            await showBrowserNotification({
                title,
                body: message,

                tag: orderId
                    ? `${type}-${orderId}`
                    : type,

                // prilagodi tvojoj ruti
                url: `/admin/local/${localId}/orders`
            });
        }
    };


    if (loading) {
        return <Loader />;
    }

    return (
        <>
            {isOrderInfoModalOpen && (
                <OrderInfoModalView
                    selectedOrderId={selectedOrderId}
                    onClose={() => {
                        setIsOrderInfoModalOpen(false)
                        fetchOrders();
                    }}
                />
            )}

            {!notificationsEnabled && (
                <button onClick={enableNotifications}>
                    🔔 Uključi obaveštenja
                </button>
            )}

            <section className="live-order-view1-section dine-in-orders">
                <div className="w-layout-blockcontainer container live-order-main w-container">
                    <div className="live-order-main-wrapper">
                        <div className="tables-status-layout">
                            <div className="lo-status-list orders-list">
                                <div className="admin-settings-box-div live-orders-info">
                                    <div className="lo-status-header-div">
                                        <div>Poručeno</div>
                                    </div>
                                    <div className="lo-status-block">
                                        <div className="lo-status-point-info ordered"></div>
                                        <div>{orders.filter(order => order.currentStatus === OrderStatusType.Ordered).length}</div>
                                    </div>
                                </div>
                                <div className="admin-settings-box-div live-orders-info">
                                    <div className="lo-status-header-div">
                                        <div>Primljeno</div>
                                    </div>
                                    <div className="lo-status-block">
                                        <div className="lo-status-point-info recived"></div>
                                        <div>{orders.filter(order => order.currentStatus === OrderStatusType.Recived).length}</div>
                                    </div>
                                </div>
                                <div className="admin-settings-box-div live-orders-info">
                                    <div className="lo-status-header-div">
                                        <div>Usluženo</div>
                                    </div>
                                    <div className="lo-status-block">
                                        <div className="lo-status-point-info served"></div>
                                        <div>{orders.filter(order => order.currentStatus === OrderStatusType.Served).length}</div>
                                    </div>
                                </div>
                                <div className="admin-settings-box-div live-orders-info">
                                    <div className="lo-status-header-div">
                                        <div>Račun</div>
                                    </div>
                                    <div className="lo-status-block">
                                        <div className="lo-status-point-info bill"></div>
                                        <div>{orders.filter(order => order.currentStatus === OrderStatusType.Bill).length}</div>
                                    </div>
                                </div>
                                <div className="admin-settings-box-div live-orders-info">
                                    <div className="lo-status-header-div">
                                        <div>Plaćeno</div>
                                    </div>
                                    <div className="lo-status-block">
                                        <div className="lo-status-point-info paid"></div>
                                        <div className="text-block-16">{orders.filter(order => order.currentStatus === OrderStatusType.Paid).length}</div>
                                    </div>
                                </div>
                                <div id="w-node-e3767345-47bf-2fa2-75fa-6bda1b7e3a97-1cee91cf" className="admin-settings-box-div live-orders-info">
                                    <div className="lo-status-header-div">
                                        <div>Otkazano</div>
                                    </div>
                                    <div className="lo-status-block">
                                        <div className="lo-status-point-info cancelled"></div>
                                        <div>{orders.filter(order => order.currentStatus === OrderStatusType.Cancelled).length}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="lo-orders-list-div">

                                {orders.length === 0 ? (
                                    <div className="no-orders-div">
                                        <div className="no-orders-icon"></div>
                                        <div className="text-block-18">Nema porudžbina za prikaz</div>
                                    </div>
                                ) : (
                                    orders.map((order) => (
                                        <div
                                            className={`admin-settings-box-div order-list-item ${orderStatusTypeToClassName(order.currentStatus)}`}
                                            key={order.id}
                                            onClick={() => setSelectedOrderId(order.id)}
                                        >
                                            <div className="lo-order-item-div">
                                                <div>
                                                    <div className="div-block-63 top-div">
                                                        <div>#{order.id.slice(0, 8)}</div>
                                                        <div className={`small-box-div order-status ${orderStatusTypeToClassName(order.currentStatus)}`}>
                                                            <div>{orderStatusTypeToString(order.currentStatus)}</div>
                                                        </div>
                                                    </div>
                                                    <div className="div-block-63">
                                                        <div className="div-block-64">
                                                            <img src={TableGreyIcon} loading="lazy" alt="" className="icon-size-16" />
                                                            <div className="description-text">Sto {order.tableNumber}</div>
                                                        </div>
                                                        <div className="div-block-64">
                                                            <img src={ClocckGreyIcon} loading="lazy" alt="" className="icon-size-16" />
                                                            <div className="description-text">{ToLocalDateTimeString(order.createdAt.toString())}</div>
                                                        </div>
                                                        <div className="div-block-64">
                                                            <img src={UserGreyIcon} loading="lazy" alt="" className="icon-size-16" />
                                                            <div className="description-text">{order.fullName}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="div-block-65">
                                                    <div className="div-block-66">
                                                        <div className="lo-order-list-item-price">{order.totalPrice} RSD</div>
                                                        <div className="description-text">{order.itemsCount} stavki</div>
                                                    </div>
                                                    <img src={ArrowRightGreyIcon} loading="lazy" alt="" className="icon-size-20 align-center"
                                                        onClick={() => {
                                                            setSelectedOrderId(order.id);
                                                            setIsOrderInfoModalOpen(true);
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}

                            </div>
                        </div>

                        <SideOrderInfo
                            selectedOrderId={selectedOrderId}
                            onStatusChange={fetchOrders}
                            listItemOrder={true}
                        />

                    </div>
                </div>
            </section>
        </>

    );
};

export default LiveOrdersDineInPage;